import { createFileRoute, Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useState, useCallback, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchAllPhotographers } from "@/lib/photographers-source";
import { SmartImg } from "@/components/SmartImg";
import { PhotoLightbox } from "@/components/PhotoLightbox";
import { detectInitialLocale } from "@/lib/i18n";

const DE_URL = "https://omselected.art/magazin/tipps-amateur-models";
const EN_URL = "https://omselected.art/magazin/tips-for-amateur-models";

type Section = { heading: string; body: string };
type Faq = { q: string; a: string };

const content = {
  de: {
    home: "Start",
    magazine: "Magazin",
    label: "Tipps & Tricks für Amateur Models",
    eyebrow: "Magazin, Models",
    title: "Tipps & Tricks für Amateur Models",
    lede: "Vor der Kamera entsteht ein gutes Bild nicht durch perfekte Posen, sondern durch Sicherheit, Vorbereitung und Vertrauen.",
    intro:
      "Du musst kein professionelles Model sein, um vor der Kamera zu überzeugen. Die meisten starken Aufnahmen entstehen, wenn du dich wohlfühlst, weißt, was dich erwartet, und der Fotograf:in vertraust. Dieser Guide bündelt die wichtigsten Tipps für dein erstes oder nächstes Shooting, von der Vorbereitung über das Posing bis zum Umgang mit Nervosität.",
    sections: [
      {
        heading: "Gut vorbereitet zum Shooting",
        body: "Bespreche vorab Stimmung, Looks und Location. Bringe mehrere Outfits in ruhigen Farben mit, achte auf gepflegte Haut und Nägel und schlafe genug. Eine kleine Auswahl an Referenzbildern hilft, eine gemeinsame Bildsprache zu finden.",
      },
      {
        heading: "Natürliches Posing lernen",
        body: "Vermeide steife Haltungen. Verlagere dein Gewicht auf ein Bein, halte die Schultern locker und bewege dich langsam zwischen den Posen. Hände sind oft der Knackpunkt: lass sie entspannt, statt sie bewusst zu platzieren. Bewegung wirkt fast immer lebendiger als ein eingefrorenes Lächeln.",
      },
      {
        heading: "Mimik und Ausdruck",
        body: "Übe Ausdrücke vor dem Spiegel: ein leichtes Lächeln, ein ernster Blick, ein Lachen. Denke an etwas Echtes, das verändert deinen Blick sofort. Atme aus, kurz bevor das Bild entsteht, das entspannt das Gesicht und nimmt Anspannung aus den Augen.",
      },
      {
        heading: "Mit Nervosität umgehen",
        body: "Nervosität ist normal. Sprich offen mit der Fotograf:in, sie führt dich durch das Shooting. Musik, ein kurzes Aufwärmen und ein paar Testbilder zu Beginn nehmen den Druck. Mit jeder Aufnahme wirst du sicherer.",
      },
      {
        heading: "Vertrauen und Grenzen",
        body: "Ein gutes Shooting basiert auf Respekt. Kläre vorab, welche Art von Bildern entstehen und wie sie verwendet werden. Du darfst jederzeit Pausen machen oder Grenzen setzen, seriöse Fotograf:innen begrüßen das.",
      },
      {
        heading: "Was bedeutet TFP?",
        body: "TFP steht für „Time for Print“ (auch „Time for Pictures“). Statt Geld tauschen Fotograf:in und Model ihre Zeit: Beide arbeiten unentgeltlich zusammen und erhalten die fertigen Bilder zur eigenen Nutzung, etwa fürs Portfolio oder Social Media. Für Amateur Models ist TFP ein idealer Einstieg, um Erfahrung zu sammeln und ein Portfolio aufzubauen. Wichtig ist eine klare Absprache: Wer bekommt welche Bilder, wie dürfen sie verwendet werden und gibt es eine schriftliche Vereinbarung (Model Release)?",
      },
    ] as Section[],
    pullQuote: "Das beste Bild entsteht in dem Moment, in dem du vergisst, dass du fotografiert wirst.",
    checklistHeading: "Deine Checkliste fürs Shooting",
    checklist: [
      "Looks, Stimmung und Location vorab besprechen",
      "Mehrere Outfits in ruhigen Farben einpacken",
      "Ausreichend schlafen und Wasser trinken",
      "Referenzbilder als Inspiration mitbringen",
      "Posing und Mimik vorher kurz üben",
      "Fragen und Grenzen offen ansprechen",
    ],
    faqHeading: "Häufige Fragen",
    faq: [
      {
        q: "Muss ich für ein Shooting Erfahrung als Model haben?",
        a: "Nein. Eine gute Fotograf:in führt dich durch das Shooting und gibt klare Anweisungen. Vorbereitung und Offenheit sind wichtiger als Erfahrung.",
      },
      {
        q: "Was soll ich zum Shooting anziehen?",
        a: "Mehrere Outfits in ruhigen Farben und gut sitzenden Schnitten. Vermeide laute Muster und Logos, sie lenken vom Gesicht ab.",
      },
      {
        q: "Wie gehe ich mit Nervosität um?",
        a: "Sprich offen mit der Fotograf:in, wärme dich kurz auf und starte mit lockeren Testbildern. Mit jeder Aufnahme wirst du entspannter.",
      },
      {
        q: "Was bedeutet TFP bei einem Shooting?",
        a: "TFP heißt „Time for Print“ bzw. „Time for Pictures“: Fotograf:in und Model arbeiten ohne Bezahlung zusammen und erhalten die Bilder zur eigenen Nutzung. Ideal, um als Amateur Model Erfahrung und ein Portfolio aufzubauen, am besten mit klarer schriftlicher Absprache.",
      },
    ] as Faq[],
    tfpRequestHeading: "TFP-Anfrage: Beispiel-Text",
    tfpRequestIntro: "So formulierst du eine kurze, seriöse Anfrage für ein TFP-Shooting. Kopiere den Text, passe Namen und Details an und sende ihn ab.",
    tfpRequestSubject: "TFP-Shooting-Anfrage: [Thema/Stil]",
    tfpRequestBody: [
      "Hallo [Name der Fotograf:in],",
      "",
      "mein Name ist [Dein Name] und ich interessiere mich für ein TFP-Shooting im Bereich [Portrait/Fashion/Editorial/etc.]. Ich habe dein Portfolio gesehen und finde deinen Stil, besonders [konkretes Bild oder Serie], sehr inspirierend.",
      "",
      "Ich bringe folgende Erfahrung mit: [kurz beschreiben, z. B. erstes Shooting / gelegentliche TFP-Erfahrung / kleines Portfolio auf Instagram].",
      "",
      "Meine Idee: [kurz Stil, Location, Stimmung, Outlines skizzieren, z. B. natürliches Tageslicht, urbaner Hintergrund, ruhige Farben].",
      "",
      "Verfügbarkeit: [Wochentage / Wochenenden, Zeitraum].",
      "",
      "Ich würde mich freuen, gemeinsam ein paar starke Bilder zu kreieren. Wenn du Interesse hast, schlage ich vor, wir besprechen Details kurz telefonisch oder per Video.",
      "",
      "Liebe Grüße",
      "[Dein Name]",
      "[Instagram / Portfolio-Link]",
    ],
    tfpRequestCopy: "Text kopieren",
    tfpRequestCopied: "Kopiert",
    photographerChecklistHeading: "Checkliste für Fotograf:innen",
    photographerChecklistIntro: "Diese Checkliste hilft dir, dein TFP-Shooting professionell vorzubereiten – von der Absprache bis zur Nachbearbeitung.",
    photographerChecklistItems: [
      "Shooting-Konzept, Stimmung und Location vorab festlegen",
      "Beleuchtung und Equipment checken (Akkus, Speicherkarten, Objektive)",
      "Klare Absprache über Bildnutzung und Model Release",
      "Outfit- und Make-up-Vorschläge an das Model kommunizieren",
      "Pufferzeit für Aufbau, Testbilder und Pausen einplanen",
      "Referenzbilder mit dem Model teilen, um eine gemeinsame Vision zu schaffen",
      "Datensicherung direkt nach dem Shooting organisieren",
      "Bildauswahl und Nachbearbeitung im vereinbarten Zeitrahmen liefern",
    ],
    printChecklist: "PDF herunterladen",
    ctaHeading: "Bereit für dein erstes Shooting?",
    ctaBody:
      "Beschreibe dein Vorhaben in einem kurzen Briefing, wir verbinden dich mit passenden, kuratierten Fotograf:innen.",
    sendRequest: "Anfrage senden",
    fashionHeading: "Inspiration: Fashion & Editorial",
    fashionIntro: "Eine Auswahl an Fashion- und Editorial-Arbeiten kuratierter Fotograf:innen, als Inspiration für dein nächstes Shooting.",
    fashionViewProfile: "Zum Profil →",
    fashionClose: "Schließen",
    fashionPrev: "Vorheriges Bild",
    fashionNext: "Nächstes Bild",
  },
  en: {
    home: "Home",
    magazine: "Magazine",
    label: "Tips & Tricks for Amateur Models",
    eyebrow: "Magazine, Models",
    title: "Tips & Tricks for Amateur Models",
    lede: "A great photo isn't made by perfect poses, but by confidence, preparation and trust in front of the camera.",
    intro:
      "You don't need to be a professional model to shine in front of the camera. Most strong shots happen when you feel at ease, know what to expect and trust your photographer. This guide gathers the most important tips for your first or next shoot, from preparation and posing to handling nerves.",
    sections: [
      {
        heading: "Arrive well prepared",
        body: "Discuss mood, looks and location beforehand. Bring several outfits in calm colours, look after your skin and nails, and get enough sleep. A small selection of reference images helps you find a shared visual language.",
      },
      {
        heading: "Learn natural posing",
        body: "Avoid stiff poses. Shift your weight onto one leg, keep your shoulders relaxed and move slowly between poses. Hands are often the tricky part: keep them relaxed rather than consciously placed. Movement almost always looks more alive than a frozen smile.",
      },
      {
        heading: "Expression and emotion",
        body: "Practise expressions in front of a mirror: a soft smile, a serious gaze, a laugh. Think of something real, it changes your eyes instantly. Exhale just before the shot is taken, it relaxes the face and softens the eyes.",
      },
      {
        heading: "Handling nerves",
        body: "Nerves are normal. Talk openly with your photographer, they will guide you through the shoot. Music, a short warm-up and a few test shots at the start take the pressure off. You'll grow more confident with every frame.",
      },
      {
        heading: "Trust and boundaries",
        body: "A good shoot is built on respect. Clarify in advance what kind of images will be taken and how they'll be used. You can pause or set boundaries at any time, reputable photographers welcome it.",
      },
      {
        heading: "What does TFP mean?",
        body: "TFP stands for \"Time for Print\" (also \"Time for Pictures\"). Instead of money, photographer and model trade their time: both collaborate for free and receive the finished images to use themselves, for example for a portfolio or social media. For amateur models, TFP is an ideal way to gain experience and build a portfolio. What matters is a clear agreement: who gets which images, how they may be used and whether there's a written model release.",
      },
    ] as Section[],
    pullQuote: "The best photo happens the moment you forget you're being photographed.",
    checklistHeading: "Your shoot checklist",
    checklist: [
      "Discuss looks, mood and location in advance",
      "Pack several outfits in calm colours",
      "Get enough sleep and stay hydrated",
      "Bring reference images for inspiration",
      "Practise posing and expressions beforehand",
      "Raise questions and boundaries openly",
    ],
    faqHeading: "Frequently asked questions",
    faq: [
      {
        q: "Do I need modelling experience for a shoot?",
        a: "No. A good photographer guides you through the shoot and gives clear directions. Preparation and openness matter more than experience.",
      },
      {
        q: "What should I wear to a shoot?",
        a: "Several outfits in calm colours and well-fitting cuts. Avoid loud patterns and logos, they distract from your face.",
      },
      {
        q: "How do I deal with nerves?",
        a: "Talk openly with your photographer, warm up briefly and start with relaxed test shots. You'll feel more at ease with every frame.",
      },
      {
        q: "What does TFP mean at a shoot?",
        a: "TFP stands for \"Time for Print\" or \"Time for Pictures\": photographer and model work together without payment and each receive the images to use themselves. It's ideal for amateur models to gain experience and build a portfolio, best with a clear written agreement.",
      },
    ] as Faq[],
    tfpRequestHeading: "TFP request: sample text",
    tfpRequestIntro: "Here's how to write a short, professional request for a TFP shoot. Copy the text, adjust names and details, and send it off.",
    tfpRequestSubject: "TFP shoot request: [theme/style]",
    tfpRequestBody: [
      "Hello [photographer's name],",
      "",
      "My name is [your name] and I'm interested in a TFP shoot in the area of [portrait/fashion/editorial/etc.]. I saw your portfolio and find your style, especially [specific image or series], very inspiring.",
      "",
      "Here's my experience so far: [briefly describe, e.g. first shoot / occasional TFP experience / small portfolio on Instagram].",
      "",
      "My idea: [briefly sketch style, location, mood, e.g. natural daylight, urban backdrop, muted colours].",
      "",
      "Availability: [weekdays / weekends, time frame].",
      "",
      "I'd love to create some strong images together. If you're interested, I suggest we discuss the details briefly by phone or video call.",
      "",
      "Kind regards",
      "[Your name]",
      "[Instagram / portfolio link]",
    ],
    tfpRequestCopy: "Copy text",
    tfpRequestCopied: "Copied",
    photographerChecklistHeading: "Checklist for Photographers",
    photographerChecklistIntro: "This checklist helps you prepare your TFP shoot professionally – from agreement to post-production.",
    photographerChecklistItems: [
      "Define concept, mood and location in advance",
      "Check lighting and equipment (batteries, memory cards, lenses)",
      "Agree clearly on image usage and model release",
      "Communicate outfit and make-up suggestions to the model",
      "Plan buffer time for setup, test shots and breaks",
      "Share reference images with the model to create a shared vision",
      "Organise data backup immediately after the shoot",
      "Deliver image selection and retouching within the agreed timeframe",
    ],
    printChecklist: "Download PDF",
    ctaHeading: "Ready for your first shoot?",
    ctaBody:
      "Describe your project in a short briefing, and we'll connect you with matching, curated photographers.",
    sendRequest: "Send a request",
    fashionHeading: "Inspiration: Fashion & Editorial",
    fashionIntro: "A selection of fashion and editorial work by curated photographers, as inspiration for your next shoot.",
    fashionViewProfile: "View profile →",
    fashionClose: "Close",
    fashionPrev: "Previous image",
    fashionNext: "Next image",
  },
};

export const Route = createFileRoute("/magazin/tipps-amateur-models")({
  head: () => {
    const isEn = detectInitialLocale() === "en";
    const c = isEn ? content.en : content.de;
    const url = isEn ? EN_URL : DE_URL;
    const description = isEn
      ? "Tips & tricks for amateur models: how to prepare, pose naturally, master expression and handle nerves for a great photo shoot."
      : "Tipps & Tricks für Amateur Models: So bereitest du dich vor, posierst natürlich, meisterst Mimik und bleibst trotz Nervosität entspannt.";
    return {
      meta: [
        { title: `${c.title}, OM Selected` },
        { name: "description", content: description },
        { property: "og:title", content: `${c.title}, OM Selected` },
        { property: "og:description", content: description },
        { property: "og:type", content: "article" },
        { property: "og:url", content: url },
      ],
      links: [
        { rel: "canonical", href: url },
        { rel: "alternate", hrefLang: "de", href: DE_URL },
        { rel: "alternate", hrefLang: "en", href: EN_URL },
        { rel: "alternate", hrefLang: "x-default", href: DE_URL },
      ],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: c.title,
            description,
            url,
            inLanguage: isEn ? "en" : "de",
            isPartOf: { "@type": "WebSite", name: "OM Selected", url: "https://omselected.art" },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: c.faq.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Start", item: "https://omselected.art/" },
              { "@type": "ListItem", position: 2, name: "Magazin", item: "https://omselected.art/magazin/" },
              { "@type": "ListItem", position: 3, name: c.label, item: url },
            ],
          }),
        },
      ],
    };
  },
  component: AmateurModelsPage,
});

function AmateurModelsPage() {
  const { i18n } = useTranslation();
  const c = i18n.language === "en" ? content.en : content.de;
  const [copied, setCopied] = useState(false);

  const { data: photographers } = useQuery({
    queryKey: ["photographers"],
    queryFn: fetchAllPhotographers,
    staleTime: 5 * 60 * 1000,
  });

  // Pick one Fashion/Editorial example per photographer (no duplicates),
  // freshly shuffled on each page load.
  const fashionExamples = useMemo(() => {
    if (!photographers?.length) return [];
    const picks: { id: string; name: string; image: string }[] = [];
    // Shuffle photographers so different ones appear on each load.
    const shuffledPhotographers = [...photographers].sort(() => Math.random() - 0.5);
    for (const p of shuffledPhotographers) {
      const themesAll = p.galleryThemesAll ?? [];
      // Collect ALL fashion/editorial images, then pick a random one.
      const matchingIdx = p.gallery
        .map((_, i) => i)
        .filter((i) =>
          (themesAll[i] ?? []).some((t) => t === "fashion" || t === "editorial"),
        );
      if (matchingIdx.length > 0) {
        const idx = matchingIdx[Math.floor(Math.random() * matchingIdx.length)];
        picks.push({ id: p.id, name: p.name, image: p.gallery[idx] });
      }
    }
    return picks.slice(0, 8);
  }, [photographers]);

  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const closeLightbox = useCallback(() => setLightboxIndex(null), []);
  const showPrev = useCallback(
    () => setLightboxIndex((i) => (i === null ? i : (i - 1 + fashionExamples.length) % fashionExamples.length)),
    [fashionExamples.length],
  );
  const showNext = useCallback(
    () => setLightboxIndex((i) => (i === null ? i : (i + 1) % fashionExamples.length)),
    [fashionExamples.length],
  );

  const handleCopy = useCallback(() => {
    const text = [c.tfpRequestSubject, "", ...c.tfpRequestBody].join("\n");
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [c]);

  const handlePrint = useCallback(async () => {
    const { jsPDF } = await import("jspdf");
    const doc = new jsPDF({ unit: "mm", format: "a4" });
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const mx = 20; // margin x
    const contentW = pageW - mx * 2;

    // Brand palette (monochrome editorial — matches website)
    const ink: [number, number, number] = [38, 38, 38];
    const muted: [number, number, number] = [120, 120, 120];
    const accent: [number, number, number] = [13, 13, 13]; // deep ink
    const hair: [number, number, number] = [232, 228, 221];
    const paper: [number, number, number] = [245, 243, 238];

    // Page background
    doc.setFillColor(...paper);
    doc.rect(0, 0, pageW, pageH, "F");

    // Top accent bar
    doc.setFillColor(...accent);
    doc.rect(0, 0, pageW, 6, "F");

    // Wordmark
    doc.setFont("times", "normal");
    doc.setFontSize(20);
    doc.setTextColor(...ink);
    doc.text("OM", mx, 24);
    const omW = doc.getTextWidth("OM ");
    doc.setTextColor(...muted);
    doc.text("Selected", mx + omW, 24);

    // Eyebrow
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(...muted);
    doc.text("OM SELECTED MAGAZINE", pageW - mx, 22, { align: "right" });

    // Title
    doc.setFont("times", "normal");
    doc.setFontSize(26);
    doc.setTextColor(...ink);
    const titleLines = doc.splitTextToSize(c.photographerChecklistHeading, contentW);
    let y = 44;
    doc.text(titleLines, mx, y);
    y += titleLines.length * 10 + 2;

    // Intro
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10.5);
    doc.setTextColor(...muted);
    const introLines = doc.splitTextToSize(c.photographerChecklistIntro, contentW);
    doc.text(introLines, mx, y);
    y += introLines.length * 5.2 + 6;

    // Divider
    doc.setDrawColor(...hair);
    doc.setLineWidth(0.4);
    doc.line(mx, y, pageW - mx, y);
    y += 10;

    // Checklist items
    c.photographerChecklistItems.forEach((item, i) => {
      const num = String(i + 1).padStart(2, "0");
      const textX = mx + 16;
      const lines = doc.splitTextToSize(item, contentW - 16) as string[];

      // Checkbox
      doc.setDrawColor(...accent);
      doc.setLineWidth(0.5);
      doc.rect(mx, y - 3.6, 4.5, 4.5);

      // Number
      doc.setFont("times", "normal");
      doc.setFontSize(12);
      doc.setTextColor(...accent);
      doc.text(num, mx + 7, y);

      // Item text
      doc.setFont("helvetica", "normal");
      doc.setFontSize(11);
      doc.setTextColor(...ink);
      doc.text(lines, textX, y);

      y += lines.length * 5.4 + 5.5;
    });

    // Footer
    doc.setDrawColor(...hair);
    doc.line(mx, pageH - 18, pageW - mx, pageH - 18);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    doc.setTextColor(...muted);
    doc.text("omselected.art", mx, pageH - 12);
    doc.text(
      i18n.language === "en" ? "TFP shoot checklist for photographers" : "TFP-Shooting-Checkliste für Fotograf:innen",
      pageW - mx,
      pageH - 12,
      { align: "right" },
    );

    doc.save(i18n.language === "en" ? "om-selected-checklist.pdf" : "om-selected-checkliste.pdf");
  }, [c, i18n.language]);

  return (
    <div className="bg-background text-foreground">
      {/* Hero */}
      <section className="border-b border-border px-5 md:px-10">
        <div className="mx-auto max-w-[1280px] pt-12 md:pt-20 pb-16 md:pb-24">
          <nav aria-label="Breadcrumb" className="eyebrow text-muted-foreground mb-8">
            <Link to="/" className="hover:text-foreground">{c.home}</Link>
            <span className="mx-2">/</span>
            <span className="text-foreground">{c.label}</span>
          </nav>
          <span className="eyebrow text-muted-foreground block mb-6">{c.eyebrow}</span>
          <h1 className="serif text-4xl md:text-7xl leading-[0.95] tracking-tight max-w-4xl">
            {c.title}
          </h1>
          <p className="serif italic text-xl md:text-3xl mt-8 max-w-2xl leading-snug">
            {c.lede}
          </p>
          <div className="mt-10 flex flex-wrap gap-4 eyebrow">
            <Link to="/brief" className="bg-foreground text-background px-8 py-4 hover:bg-foreground/85 transition-colors">
              {c.sendRequest}
            </Link>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="px-5 md:px-10 border-b border-border">
        <div className="mx-auto max-w-[760px] py-16 md:py-24">
          <p className="text-lg md:text-xl leading-relaxed text-muted-foreground">{c.intro}</p>
        </div>
      </section>

      {/* Editorial sections */}
      <section className="px-5 md:px-10 border-b border-border">
        <div className="mx-auto max-w-[760px] py-16 md:py-24 space-y-14">
          {c.sections.map((s) => (
            <article key={s.heading}>
              <h2 className="serif text-2xl md:text-3xl mb-4">{s.heading}</h2>
              <p className="text-base md:text-lg leading-relaxed text-muted-foreground">{s.body}</p>
            </article>
          ))}
        </div>
      </section>

      {/* Pull quote */}
      <section className="px-5 md:px-10 border-b border-border bg-[oklch(0.16_0_0)] text-background">
        <div className="mx-auto max-w-[900px] py-20 md:py-28 text-center">
          <blockquote className="serif italic text-2xl md:text-4xl leading-tight">
            “{c.pullQuote}”
          </blockquote>
        </div>
      </section>

      {/* Fashion & Editorial examples */}
      {fashionExamples.length > 0 && (
        <section className="px-5 md:px-10 border-b border-border">
          <div className="mx-auto max-w-[1280px] py-16 md:py-24">
            <h2 className="serif text-2xl md:text-3xl mb-4">{c.fashionHeading}</h2>
            <p className="text-muted-foreground mb-10 leading-relaxed max-w-[760px]">{c.fashionIntro}</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
              {fashionExamples.map((ex, i) => (
                <button
                  key={ex.id}
                  type="button"
                  onClick={() => setLightboxIndex(i)}
                  className="group block text-left cursor-zoom-in"
                >
                  <div className="aspect-[3/4] overflow-hidden">
                    <SmartImg
                      source={ex.image}
                      transform={{ width: 600, resize: "contain" }}
                      alt={ex.name}
                      loading="lazy"
                      className="h-full w-full object-contain transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <span className="eyebrow text-muted-foreground mt-2 block group-hover:text-foreground transition-colors">
                    {ex.name}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Fashion & Editorial lightbox */}
      <PhotoLightbox
        info={lightboxIndex !== null && fashionExamples[lightboxIndex] ? {
          image: fashionExamples[lightboxIndex].image,
          photographerName: fashionExamples[lightboxIndex].name,
          photographerId: fashionExamples[lightboxIndex].id,
          creatorType: "photographer" as const,
        } : null}
        onClose={closeLightbox}
        onPrev={fashionExamples.length > 1 ? showPrev : undefined}
        onNext={fashionExamples.length > 1 ? showNext : undefined}
        neighborImages={lightboxIndex !== null ? [
          fashionExamples[(lightboxIndex - 1 + fashionExamples.length) % fashionExamples.length]?.image,
          fashionExamples[(lightboxIndex + 1) % fashionExamples.length]?.image,
        ] : undefined}
      />

      {/* TFP Request Template */}
      <section className="px-5 md:px-10 border-b border-border bg-muted/40">
        <div className="mx-auto max-w-[760px] py-16 md:py-24">
          <details className="group" open>
            <summary className="serif text-2xl md:text-3xl cursor-pointer list-none flex justify-between items-center gap-4 mb-4">
              {c.tfpRequestHeading}
              <span className="text-muted-foreground transition-transform group-open:rotate-45">+</span>
            </summary>
            <p className="text-muted-foreground mb-8 leading-relaxed">{c.tfpRequestIntro}</p>
            <div className="bg-background border border-border p-6 md:p-8 rounded-sm">
              <p className="eyebrow text-muted-foreground mb-4">{c.tfpRequestSubject}</p>
              <pre className="text-sm md:text-base leading-relaxed whitespace-pre-wrap font-sans text-foreground">
                {c.tfpRequestBody.join("\n")}
              </pre>
            </div>
            <button
              type="button"
              onClick={handleCopy}
              className="mt-6 eyebrow inline-flex items-center gap-2 bg-foreground text-background px-6 py-3 hover:bg-foreground/85 transition-colors"
            >
              {copied ? c.tfpRequestCopied : c.tfpRequestCopy}
            </button>
          </details>
        </div>
      </section>

      {/* Checklist */}
      <section className="px-5 md:px-10 border-b border-border">
        <div className="mx-auto max-w-[760px] py-16 md:py-24">
          <h2 className="serif text-2xl md:text-3xl mb-8">{c.checklistHeading}</h2>
          <ul className="space-y-4">
            {c.checklist.map((item, i) => (
              <li key={item} className="flex gap-4 items-baseline">
                <span className="serif text-muted-foreground text-lg shrink-0">{String(i + 1).padStart(2, "0")}</span>
                <span className="text-base md:text-lg leading-relaxed">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Photographer Checklist */}
      <section className="px-5 md:px-10 border-b border-border print-checklist">
        <div className="mx-auto max-w-[760px] py-16 md:py-24">
          <h2 className="serif text-2xl md:text-3xl mb-4">{c.photographerChecklistHeading}</h2>
          <p className="text-muted-foreground mb-8 leading-relaxed">{c.photographerChecklistIntro}</p>
          <ul className="space-y-4">
            {c.photographerChecklistItems.map((item, i) => (
              <li key={item} className="flex gap-4 items-baseline">
                <span className="serif text-muted-foreground text-lg shrink-0">{String(i + 1).padStart(2, "0")}</span>
                <span className="text-base md:text-lg leading-relaxed">{item}</span>
              </li>
            ))}
          </ul>
          <button
            type="button"
            onClick={handlePrint}
            className="mt-8 eyebrow inline-flex items-center gap-2 border border-foreground px-6 py-3 hover:bg-muted transition-colors"
          >
            {c.printChecklist}
          </button>
        </div>
      </section>

      {/* FAQ */}
      <section className="px-5 md:px-10 border-b border-border">
        <div className="mx-auto max-w-[760px] py-16 md:py-24">
          <h2 className="serif text-3xl md:text-4xl mb-10">{c.faqHeading}</h2>
          <div className="divide-y divide-border border-t border-border">
            {c.faq.map((f) => (
              <details key={f.q} className="group py-5">
                <summary className="serif text-lg md:text-xl cursor-pointer list-none flex justify-between items-center gap-4">
                  {f.q}
                  <span className="text-muted-foreground transition-transform group-open:rotate-45">+</span>
                </summary>
                <p className="mt-4 text-base leading-relaxed text-muted-foreground">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-5 md:px-10">
        <div className="mx-auto max-w-[900px] py-20 md:py-28 text-center">
          <h2 className="serif text-3xl md:text-5xl mb-6">{c.ctaHeading}</h2>
          <p className="text-muted-foreground max-w-xl mx-auto mb-10 leading-relaxed">
            {c.ctaBody}
          </p>
          <Link to="/brief" className="eyebrow inline-block bg-foreground text-background px-10 py-4 hover:bg-foreground/85 transition-colors">
            {c.sendRequest}
          </Link>
        </div>
      </section>
    </div>
  );
}